import React, { useState } from 'react';
import { Plus, FileText, Edit, Copy, Trash2, Download, Eye, Send } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';

const ProposalTemplates = () => {
  const [showCreateTemplate, setShowCreateTemplate] = useState(false);
  const [newTemplate, setNewTemplate] = useState({
    name: '',
    description: '',
    content: ''
  });

  const templates = [
    {
      id: 1,
      name: 'Leadership Training Proposal',
      description: 'Comprehensive proposal template for leadership development programs',
      category: 'Leadership',
      lastModified: '2024-03-10',
      timesUsed: 15,
      content: `[COMPANY LOGO]

LEADERSHIP DEVELOPMENT PROGRAM PROPOSAL

Prepared for: [CLIENT NAME]
Prepared by: [YOUR NAME]
Date: [DATE]

EXECUTIVE SUMMARY
This proposal outlines a comprehensive leadership development program designed to enhance the leadership capabilities of your management team. Our proven methodology combines theoretical frameworks with practical application to deliver measurable results.

PROGRAM OVERVIEW
Duration: [PROGRAM DURATION]
Participants: [NUMBER OF PARTICIPANTS]
Format: [IN-PERSON/VIRTUAL/HYBRID]
Investment: [PROGRAM COST]

LEARNING OBJECTIVES
1. Develop authentic leadership presence
2. Master effective communication strategies
3. Build high-performing teams
4. Navigate change and uncertainty
5. Drive organizational results

PROGRAM MODULES
Module 1: Leadership Self-Awareness
Module 2: Communication Excellence
Module 3: Team Dynamics
Module 4: Strategic Thinking
Module 5: Change Leadership

METHODOLOGY
- Interactive workshops
- Case study analysis
- 360-degree feedback
- Action learning projects
- Peer coaching sessions

FACILITATOR CREDENTIALS
[YOUR CREDENTIALS AND EXPERIENCE]

INVESTMENT AND NEXT STEPS
Program Investment: [AMOUNT]
Includes: All materials, assessments, and follow-up support

Next Steps:
1. Review and approve proposal
2. Schedule program kickoff
3. Conduct pre-program assessments
4. Begin program delivery

We look forward to partnering with you to develop exceptional leaders.

Best regards,
[YOUR NAME]
[YOUR TITLE]
[CONTACT INFORMATION]`
    },
    {
      id: 2,
      name: 'Corporate Workshop Proposal',
      description: 'Standard template for one-day corporate training workshops',
      category: 'Workshop',
      lastModified: '2024-03-08',
      timesUsed: 28,
      content: `CORPORATE TRAINING WORKSHOP PROPOSAL

Workshop Title: [WORKSHOP TITLE]
Client: [CLIENT NAME]
Date: [WORKSHOP DATE]
Duration: [DURATION]
Location: [LOCATION]

WORKSHOP OVERVIEW
[Brief description of the workshop and its benefits]

TARGET AUDIENCE
[Description of intended participants]

LEARNING OUTCOMES
By the end of this workshop, participants will be able to:
- [Outcome 1]
- [Outcome 2]
- [Outcome 3]
- [Outcome 4]

AGENDA
9:00 AM - Welcome & Introductions
9:30 AM - [Session 1 Title]
10:30 AM - Break
10:45 AM - [Session 2 Title]
12:00 PM - Lunch
1:00 PM - [Session 3 Title]
2:30 PM - Break
2:45 PM - [Session 4 Title]
4:00 PM - Action Planning
4:30 PM - Wrap-up & Next Steps
5:00 PM - Conclusion

FACILITATOR PROFILE
[Your background and expertise]

INVESTMENT
Workshop Fee: [AMOUNT]
Includes: Materials, lunch, and follow-up resources

TERMS AND CONDITIONS
[Payment terms, cancellation policy, etc.]

Thank you for considering our services.

[YOUR NAME]
[CONTACT DETAILS]`
    },
    {
      id: 3,
      name: 'Executive Coaching Proposal',
      description: 'Template for individual executive coaching engagements',
      category: 'Coaching',
      lastModified: '2024-03-05',
      timesUsed: 12,
      content: `EXECUTIVE COACHING PROPOSAL

CONFIDENTIAL

Coaching Engagement for: [EXECUTIVE NAME]
Organization: [COMPANY NAME]
Coach: [YOUR NAME]
Date: [DATE]

COACHING OVERVIEW
Executive coaching is a collaborative partnership designed to maximize leadership potential and drive organizational results. This engagement focuses on developing authentic leadership capabilities while achieving specific business objectives.

COACHING OBJECTIVES
Primary Goals:
- [Objective 1]
- [Objective 2]
- [Objective 3]

Success Metrics:
- [Metric 1]
- [Metric 2]
- [Metric 3]

COACHING APPROACH
Our coaching methodology includes:
- 360-degree feedback assessment
- Leadership style analysis
- Goal setting and action planning
- Regular coaching sessions
- Progress reviews and adjustments

ENGAGEMENT STRUCTURE
Duration: [ENGAGEMENT LENGTH]
Format: [IN-PERSON/VIRTUAL/HYBRID]
Frequency: [SESSION FREQUENCY]
Session Length: [SESSION DURATION]

COACH CREDENTIALS
[Your qualifications, certifications, and experience]

INVESTMENT
Coaching Fee: [AMOUNT]
Payment Terms: [TERMS]

CONFIDENTIALITY
All coaching conversations and materials are strictly confidential.

NEXT STEPS
1. Sign coaching agreement
2. Complete initial assessments
3. Schedule first coaching session
4. Begin coaching journey

We're excited to partner with you on this development journey.

[YOUR NAME], [CREDENTIALS]
Executive Coach
[CONTACT INFORMATION]`
    }
  ];

  const handleCreateTemplate = () => {
    // Here you would typically save the template
    console.log('Creating template:', newTemplate);
    setNewTemplate({ name: '', description: '', content: '' });
    setShowCreateTemplate(false);
  };

  const handleEditTemplate = (template) => {
    // Open template in edit mode
    setNewTemplate({
      name: template.name,
      description: template.description,
      content: template.content
    });
    setShowCreateTemplate(true);
  };

  const handleDownloadTemplate = (template) => {
    // Create a blob with the template content
    const blob = new Blob([template.content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    
    // Create download link and trigger download
    const link = document.createElement('a');
    link.href = url;
    link.download = `${template.name.replace(/\s+/g, '_')}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handlePreviewTemplate = (template) => {
    // Open template content in a new window for preview
    const previewWindow = window.open('', '_blank', 'width=800,height=600');
    previewWindow.document.write(`
      <html>
        <head>
          <title>${template.name} - Preview</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; line-height: 1.6; }
            pre { white-space: pre-wrap; word-wrap: break-word; }
          </style>
        </head>
        <body>
          <h1>${template.name}</h1>
          <p><strong>Description:</strong> ${template.description}</p>
          <hr>
          <pre>${template.content}</pre>
        </body>
      </html>
    `);
    previewWindow.document.close();
  };

  const handleDuplicateTemplate = (template) => {
    setNewTemplate({
      name: `${template.name} (Copy)`,
      description: template.description,
      content: template.content
    });
    setShowCreateTemplate(true);
  };

  const getStatusColor = (category) => {
    switch (category) {
      case 'Leadership': return 'bg-blue-100 text-blue-800';
      case 'Workshop': return 'bg-green-100 text-green-800';
      case 'Coaching': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Proposal Templates</span>
            <Dialog open={showCreateTemplate} onOpenChange={setShowCreateTemplate}>
              <DialogTrigger asChild>
                <Button className="bg-green-600 hover:bg-green-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Template
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Create New Proposal Template</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium">Template Name</label>
                    <Input
                      placeholder="e.g. Leadership Training Proposal"
                      value={newTemplate.name}
                      onChange={(e) => setNewTemplate(prev => ({ ...prev, name: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Description</label>
                    <Input
                      placeholder="Brief description of this template"
                      value={newTemplate.description}
                      onChange={(e) => setNewTemplate(prev => ({ ...prev, description: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Template Content</label>
                    <Textarea
                      placeholder="Enter your proposal template content with placeholders like [CLIENT NAME], [DATE], etc."
                      value={newTemplate.content}
                      onChange={(e) => setNewTemplate(prev => ({ ...prev, content: e.target.value }))}
                      className="min-h-[300px] font-mono text-sm"
                    />
                  </div>
                  <div className="flex justify-end space-x-2">
                    <Button variant="outline" onClick={() => setShowCreateTemplate(false)}>
                      Cancel
                    </Button>
                    <Button onClick={handleCreateTemplate} className="bg-green-600 hover:bg-green-700">
                      Create Template
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {templates.map((template) => (
              <Card key={template.id} className="border-2 hover:border-green-200 transition-colors">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <FileText className="w-5 h-5 text-green-600" />
                        <h3 className="text-lg font-semibold">{template.name}</h3>
                        <Badge className={getStatusColor(template.category)}>
                          {template.category}
                        </Badge>
                      </div>
                      
                      <p className="text-gray-600 mb-3">{template.description}</p>
                      
                      <div className="flex items-center space-x-4 text-sm text-gray-600 mb-4">
                        <span>Modified: {template.lastModified}</span>
                        <span>•</span>
                        <span>Used: {template.timesUsed} times</span>
                      </div>
                      
                      <div className="flex items-center space-x-3">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handlePreviewTemplate(template)}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Preview
                        </Button>
                        <Button 
                          size="sm" 
                          className="bg-green-600 hover:bg-green-700"
                          onClick={() => handleEditTemplate(template)}
                        >
                          <Edit className="w-4 h-4 mr-1" />
                          Edit Template
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handleDuplicateTemplate(template)}
                        >
                          <Copy className="w-4 h-4 mr-1" />
                          Duplicate
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handleDownloadTemplate(template)}
                        >
                          <Download className="w-4 h-4 mr-1" />
                          Download
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="text-red-600 border-red-200 hover:bg-red-50"
                          onClick={() => confirm(`Are you sure you want to delete "${template.name}"?`) && console.log('Delete template:', template.id)}
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          Delete
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ProposalTemplates;